import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Card } from '../../components/primitives/index.js'
import { customerApi } from '../../lib/customerApi.js'
import { fmtMoney } from '../../utils/invoiceUtils.js'
import './AccountRewards.css'

const REASON_LABELS = {
  order_earn: 'Order', redeem: 'Redeemed at checkout', earn_reversal: 'Order cancelled',
  redeem_refund: 'Redemption refunded', signup: 'Account created', first_purchase: 'First order bonus',
  birthday: 'Birthday bonus', review: 'Review bonus', review_reversal: 'Review removed',
  referral: 'Referral reward', referral_reversal: 'Referral reversed',
  newsletter: 'Newsletter signup', expiry: 'Expired', manual: 'Adjustment',
}

function fmtDateShort(iso) {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(iso))
}

export default function AccountRewards() {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    customerApi.getRewards().then(setData).catch((e) => setError(e.message))
  }, [])

  if (error) return <Card>{error}</Card>
  if (!data) return <Card>Loading rewards&hellip;</Card>
  if (!data.enabled) return <Card>The rewards program isn&apos;t active right now — check back soon.</Card>

  const earnList = [
    [`$1 spent`, `${data.earnRate} ${data.earnRate === 1 ? 'point' : 'points'}`],
    ...(data.events?.firstPurchase ? [['First order', `+${data.events.firstPurchase}`]] : []),
    ...(data.events?.birthday ? [['Birthday (add it in Settings)', `+${data.events.birthday}`]] : []),
    ...(data.events?.newsletterSignup ? [['Newsletter opt-in', `+${data.events.newsletterSignup}`]] : []),
  ]

  return (
    <div className="account-rewards">
      <Card className="account-rewards__tier-card">
        <div className="account-rewards__tier-top">
          <div>
            <span className="account-rewards__label">{data.pointsName ?? 'Protocol Points'}</span>
            <span className="account-rewards__points-value">{data.balance}</span>
            {data.storeCredit > 0 && (
              <span className="account-rewards__tier-multiplier">+ {fmtMoney(data.storeCredit)} store credit</span>
            )}
          </div>

          {data.tiersEnabled && data.tier && (
            <div className="account-rewards__points-badge">
              <span className="account-rewards__label">Membership tier</span>
              <span className="account-rewards__tier-name" style={{ color: data.tier.color }}>{data.tier.name}</span>
              <span className="account-rewards__tier-multiplier">
                {data.tier.multiplier > 1 ? `Earning ${data.tier.multiplier}× points per dollar` : 'Standard earning rate'}
              </span>
            </div>
          )}
        </div>

        {data.tiersEnabled && data.nextTier && (
          <div className="account-rewards__progress">
            <span className="account-rewards__progress-label">
              {fmtMoney(data.nextTier.remaining)} more unlocks <strong style={{ color: data.nextTier.color }}>{data.nextTier.name}</strong>
            </span>
          </div>
        )}
      </Card>

      <h4 className="account-rewards__section-title">Redeem at checkout</h4>
      <div className="account-rewards__redemption">
        {(data.redemptions ?? []).map((r) => {
          const unlocked = data.balance >= r.points
          return (
            <Card key={r.points} className={`account-rewards__redeem-tile ${unlocked ? 'is-unlocked' : ''}`}>
              <span className="account-rewards__redeem-value">{fmtMoney(r.amount)}</span>
              <span className="account-rewards__redeem-cost">{r.points} pts</span>
              {!unlocked && <span className="account-rewards__redeem-lock" aria-hidden="true">&#128274;</span>}
            </Card>
          )
        })}
      </div>

      <h4 className="account-rewards__section-title">How to earn</h4>
      <Card className="account-rewards__earn-card">
        {earnList.map(([label, value], i) => (
          <div key={i} className="account-rewards__earn-row">
            <span>{label}</span>
            <span className="account-rewards__earn-value">{value}</span>
          </div>
        ))}
      </Card>

      {(data.ledger ?? []).length > 0 && (
        <>
          <h4 className="account-rewards__section-title">History</h4>
          <div className="account-rewards__history">
            {data.ledger.map((l, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                <Card className="account-rewards__history-row">
                  <span>
                    {REASON_LABELS[l.reason] ?? l.reason}
                    {l.reference && !l.reference.includes(':') ? ` · ${l.reference}` : ''}
                  </span>
                  <span className="account-rewards__history-date">{fmtDateShort(l.at)}</span>
                  <span className="account-rewards__history-delta" style={{ color: l.delta >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                    {l.delta >= 0 ? '+' : ''}{l.delta}
                  </span>
                </Card>
              </motion.div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
